import React, { useEffect, useRef, useState } from 'react';
import {
  Brain,
  FileText,
  Mic,
  MessageSquare,
  Layers,
  Grid,
  Image,
  Users,
  Bookmark,
} from 'lucide-react';
import { APP_FEATURES } from '../../constants/app';

const icons = { Brain, FileText, Mic, MessageSquare, Layers, Grid, Image, Users, Bookmark };

export function Features() {
  const getIconComponent = (iconName: string) => {
    return icons[iconName as keyof typeof icons];
  };
  
  // Refs and state for scroll animations that work in both directions
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update visibility based on current intersection state
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1, // Trigger when 10% of the section is visible
        rootMargin: '0px 0px -100px 0px', // Trigger a bit before the section is fully in view
      }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div ref={sectionRef} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-40">
      {/* Background gradient with scroll animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] 
            bg-gradient-to-br from-blue-500/5 to-transparent rounded-full blur-3xl 
            transition-all duration-1000 transform ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} 
        />
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {APP_FEATURES.map((feature, index) => {
          const IconComponent = getIconComponent(feature.icon);
          return (
            <div
              key={feature.title}
              className={`group glow-card inner-light hover-scale z-depth-1 inner-border-glow
                bg-gray-800/50 backdrop-blur-xl rounded-2xl p-8 border border-gray-700/50
                transform transition-all duration-700
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{ 
                transitionDelay: `${isVisible ? index * 150 : 0}ms` 
              }}
            >
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-blue-600/10 flex items-center justify-center mb-6 transition-transform group-hover:scale-110 group-hover:rotate-3 duration-300 soft-highlight">
                  <IconComponent
                    size={28}
                    className="text-blue-500 transition-transform group-hover:scale-110 duration-300"
                  />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-blue-400 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                {feature.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
